<!--
  @component ConsumerRoot — sits inside IdentityIO + ScopeIO so both hooks
                            resolve; owns routing-driven state + the auth guard
                            + the scope→currentProject bridge + the Shell/Router
                            mount.

  Responsibilities:
    1. Track the current path; derive the active Deployments section from
       `/deployments[/:section]` (for sub-header highlighting).
    2. Auth guard: signed-out users land on `/`; signed-in users on `/` bounce
       to `/projects`.
    3. Bridge ScopeIO → IdentityIO: when scope resolves to exactly one project,
       keep `currentProject` set so the per-project screens resolve. (Scope is
       the source of truth; single-project is just scope.singleId.)
    4. Mount the chain: <NetworkDeployment> → <ConsumerShell> → <Router>.
-->
<script lang="ts">
  import Router, { location, push } from "svelte-spa-router";
  import NetworkDeployment from "../../global/io/hyperedges/NetworkDeployment.svelte";
  import ConsumerShell from "../../global/visuals/ConsumerShell.svelte";
  import { useIdentityIO } from "./accounts/IdentityIO.svelte";
  import { useScopeIO } from "./features/deployments/ScopeIO.svelte";
  import { ROUTES, PROJECT_SCREENS } from "./routes.js";

  const identity = useIdentityIO();
  const scope = useScopeIO();

  let currentPath = $state("/");
  $effect(() => {
    const unsubscribe = location.subscribe((path: string) => {
      currentPath = path;
    });
    return unsubscribe;
  });

  // Active Deployments section from /deployments[/:section] (sub-header highlight).
  const DEPLOY_RE = /^\/deployments(?:\/([^/?]+))?/;
  const currentSection = $derived.by<string | null>(() => {
    const m = DEPLOY_RE.exec(currentPath);
    if (!m) return null;
    const s = m[1];
    return s && (PROJECT_SCREENS as readonly string[]).includes(s) ? s : "overview";
  });

  // Single active project = scope resolved to exactly one.
  const currentProjectId = $derived(scope.singleId);

  // ─── Auth guard ──────────────────────────────────────────────
  $effect(() => {
    if (!identity.ready) return;
    if (identity.user === null && currentPath !== "/") {
      push("/");
    } else if (identity.user !== null && currentPath === "/") {
      push("/projects");
    }
  });

  // ─── Scope → currentProject bridge ───────────────────────────
  // Keeps IdentityIO.currentProject in sync with the single-project scope so
  // the per-project screens (which read currentProject) resolve. Null when
  // scope is all/many/none.
  $effect(() => {
    if (!identity.ready) return;
    identity.selectProject(scope.singleId);
  });

  function handleNavigate(path: string): void {
    push(path);
  }
</script>

<NetworkDeployment>
  <ConsumerShell
    currentProjectId={currentProjectId}
    currentProjectScreen={currentSection}
    {currentPath}
    onNavigate={handleNavigate}
  >
    <Router routes={ROUTES} />
  </ConsumerShell>
</NetworkDeployment>
